import apiClient from '../services/apiClient';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import StakeTable, { Column } from "./GameEngine/StakeTable";
import { Calendar } from 'lucide-react';
import { FormMultiSelect } from '../components/ui';
import { formatDateTime, getPresetRangeInTimezone } from '../utils/dateUtils';

interface WithdrawalItem {
  uid: string;
  transaction_id: number;
  transaction_date: string;
  name: string;
  amount: number;
  status: string;
  utr_no: string;
  currency?: string;
  gateway_name?: string;
  withdraw_method?: string;
  reject_reason?: string;
  payment_details?: any;
}

interface GatewayItem {
  id: number;
  gateway_name: string;
  display_name: string;
  currency: string;
  status: boolean;
}

type DatePreset = "today" | "yesterday" | "week" | "month" | "custom" | "all";

const getPresetRange = (
  preset: DatePreset
): { start: string | null; end: string | null } => {
  return getPresetRangeInTimezone(preset);
};

const Withdrawals = () => {
  const currentRole = (localStorage.getItem("role") || "").toLowerCase();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [gatewayFilter, setGatewayFilter] = useState<string[]>([]);
  const [currencyFilter, setCurrencyFilter] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const [activePreset, setActivePreset] = useState<DatePreset>("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [gateways, setGateways] = useState<GatewayItem[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);

  // Fetch gateways to populate filters
  useEffect(() => {
    const fetchGateways = async () => {
      try {
        const res = await apiClient.get('/admin/withdrawal-gateways');
        if (res.data?.success && Array.isArray(res.data.data)) {
          setGateways(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching withdrawal gateways for filters:', err);
      }
    };
    fetchGateways();
  }, []);

  // Fetch currencies dynamically
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await apiClient.get('/api/currencies');
        if (res.data?.success && Array.isArray(res.data.currencies)) {
          setCurrencies(res.data.currencies.map((c: any) => c.code.toUpperCase()));
        }
      } catch (err) {
        console.error('Error fetching currencies:', err);
      }
    };
    fetchCurrencies();
  }, []);



  // Fetch withdrawals
  const { data: withdrawalData = { data: [], total: 0 }, isLoading } = useQuery({
    queryKey: ['withdrawals', page, limit, searchTerm, statusFilter, gatewayFilter, currencyFilter, activePreset, customStart, customEnd],
    queryFn: async () => {
      const offset = (page - 1) * limit;
      let url = `/getManualWithdrawalData?limit=${limit}&offset=${offset}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (statusFilter.length > 0) url += `&status=${statusFilter.join(',')}`;
      if (gatewayFilter.length > 0) url += `&gatewayName=${gatewayFilter.join(',')}`;
      if (currencyFilter.length > 0) url += `&currency=${currencyFilter.join(',')}`;

      if (activePreset === "custom") {
        if (customStart) url += `&startDate=${customStart}`;
        if (customEnd) url += `&endDate=${customEnd}`;
      } else if (activePreset !== "all") {
        const { start, end } = getPresetRange(activePreset);
        if (start) url += `&startDate=${start}`;
        if (end) url += `&endDate=${end}`;
      }

      const response = await apiClient.get(url);
      if (response.data && Array.isArray(response.data.data)) {
        return {
          data: response.data.data,
          total: response.data.total
        };
      }
      return { data: [], total: 0 };
    }
  });

  const withdrawals = withdrawalData.data;
  const total = withdrawalData.total;
  const totalPages = Math.ceil(total / limit);

  const actionMutation = useMutation({
    mutationFn: async ({ utr_no, uid, coin, amount, isApproved }: any) => {
      return apiClient.post('/approveRejectManualWithdrawal', {
        utr_no,
        uid,
        coin,
        amount,
        isApproved,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
    },
    onError: (error) => {
      console.error('Error processing manual withdrawal:', error);
    }
  });

  const handleAcceptPayment = (utrNo: string, uid: string, amount: number, currency: string) => {
    actionMutation.mutate({ utr_no: utrNo, uid, coin: currency.toLowerCase(), amount, isApproved: true });
  };

  const handleRejectPayment = (utrNo: string, uid: string, amount: number, currency: string) => {
    actionMutation.mutate({ utr_no: utrNo, uid, coin: currency.toLowerCase(), amount, isApproved: false });
  };

  const getGatewayDisplayLabel = (name: string | undefined): string => {
    if (!name) return 'Manual';
    // Check if we have a match in fetched gateways
    const gateway = gateways.find(g => g.gateway_name.toLowerCase() === name.toLowerCase());
    if (gateway) return gateway.display_name || gateway.gateway_name;

    const labelMap: { [key: string]: string } = {
      'manual_bank': 'Bank Details',
      'manual_upi_wallet': 'UPI / Wallet',
    };
    return labelMap[name.toLowerCase()] || name;
  };

  const columns: Column[] = [
    { key: 'sn', label: 'SNo.', render: (_, __, i) => i + 1 },
    { key: 'name', label: 'User Name', render: (val, item: any) => (
      <div>
        <div className="font-semibold text-white">{val}</div>
        <div className="text-xs text-slate-400">UID: {item.uid}</div>
      </div>
    ) },
    { key: 'parent_name', label: 'User Type', render: (_, item: any) => (
      <span className="text-xs text-slate-300 font-semibold uppercase">
        {item.parent_role ? `${item.parent_role}: ${item.parent_name}` : 'DIRECT USER'}
      </span>
    ) },
    { key: 'amount', label: 'Amount', render: (val, item: any) => (
      <span className="font-bold text-white">
        {Number(val).toFixed(2)} {(item.currency || 'INR').toUpperCase()}
      </span>
    ) },
    { key: 'gateway_name', label: 'Method', render: (val) => (
      <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-xs font-semibold">
        {getGatewayDisplayLabel(val)}
      </span>
    ) },
    { key: 'withdraw_method', label: 'Withdrawal Details', render: (_, item: any) => {
      let fields: { [key: string]: any } = {};
      try {
        const details = typeof item.payment_details === 'string'
          ? JSON.parse(item.payment_details)
          : item.payment_details;
        if (details && details.extra_fields) {
          fields = details.extra_fields;
        }
      } catch (e) {}

      const keys = Object.keys(fields).filter(k => k !== 'currency');
      if (keys.length > 0) {
        return (
          <div className="flex flex-col gap-0.5 text-[11px] text-slate-300 font-mono">
            {keys.map(k => (
              <div key={k} className="truncate max-w-[240px]" title={`${k.replace(/_/g, ' ').toUpperCase()}: ${fields[k]}`}>
                <span className="font-semibold text-indigo-400">{k.replace(/_/g, ' ').toUpperCase()}:</span> {fields[k]}
              </div>
            ))}
          </div>
        );
      }

      return (
        <div className="max-w-xs truncate text-xs text-slate-300 font-mono" title={item.withdraw_method}>
          {item.withdraw_method || 'N/A'}
        </div>
      );
    } },
    { key: 'utr_no', label: 'Transaction ID', render: (val) => (
      <span className="font-mono text-xs text-slate-400">{val}</span>
    ) },
    { key: 'status', label: 'Status', render: (val) => (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
        val === 'accepted' ? 'bg-green-500/20 text-green-400' :
        val === 'rejected' ? 'bg-red-500/20 text-red-400' :
        'bg-yellow-500/20 text-yellow-400'
      }`}>
        {String(val).toUpperCase()}
      </span>
    ) },
    { key: 'transaction_date', label: 'Date', render: (val) => formatDateTime(val) },
    { key: 'actions', label: 'Actions', render: (_, item: WithdrawalItem) => {
      if (item.status === 'pending' || item.status === 'In Queue') {
        return (
          <div className="flex gap-2">
            <button
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold transition-all disabled:opacity-50"
              onClick={() => handleAcceptPayment(item.utr_no, item.uid, item.amount, item.currency || 'inr')}
              disabled={actionMutation.isPending}
            >
              Accept
            </button>
            <button
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-bold transition-all disabled:opacity-50"
              onClick={() => handleRejectPayment(item.utr_no, item.uid, item.amount, item.currency || 'inr')}
              disabled={actionMutation.isPending}
            >
              Reject
            </button>
          </div>
        );
      } else if (item.status === 'accepted') {
        return <span className="text-green-500 font-bold text-xs">Accepted</span>;
      } else {
        return <span className="text-red-500 font-bold text-xs">Rejected</span>;
      }
    } }
  ];

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 min-h-screen text-white bg-[#151929]">

      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-white">
          Withdrawal Management
        </h2>
        <button
          onClick={() => setShowFilters(f => !f)}
          className="flex lg:hidden items-center gap-2 px-4 py-2 bg-[#1e2337] border border-slate-700 rounded-lg text-sm font-semibold text-slate-300 hover:text-white hover:border-indigo-500 transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>
          {showFilters ? 'Hide Filters' : 'Filters'}
          {(statusFilter.length + gatewayFilter.length + currencyFilter.length) > 0 && (
            <span className="ml-1 bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {statusFilter.length + gatewayFilter.length + currencyFilter.length}
            </span>
          )}
        </button>
      </div>

      {/* Premium Filters Section */}
      <div className={`bg-[#1e2337] p-5 rounded-xl border border-slate-700/40 mb-6 flex flex-wrap gap-4 items-start ${
        showFilters ? 'flex' : 'hidden lg:flex'
      }`}>


        {/* Method / Gateway Filter */}
        <div className="w-full sm:w-[180px]">
          <FormMultiSelect
            label="Method"
            selectedValues={gatewayFilter}
            onChange={(vals) => { setGatewayFilter(vals); setPage(1); }}
            placeholder="All Methods"
            options={[
              { value: "manual_bank", label: "Bank Details" },
              { value: "manual_upi_wallet", label: "UPI / Wallet" },
              ...gateways.map(g => ({ value: g.gateway_name, label: g.display_name || g.gateway_name }))
            ]}
          />
        </div>

        {/* Currency Filter */}
        <div className="w-full sm:w-[140px]">
          <FormMultiSelect
            label="Currency"
            selectedValues={currencyFilter}
            onChange={(vals) => { setCurrencyFilter(vals); setPage(1); }}
            placeholder="All Currencies"
            options={currencies.map(c => ({ value: c, label: c }))}
          />
        </div>



        {/* Status Filter */}
        <div className="w-full sm:w-[140px]">
          <FormMultiSelect
            label="Status"
            selectedValues={statusFilter}
            onChange={(vals) => { setStatusFilter(vals); setPage(1); }}
            placeholder="All Statuses"
            options={[
              { value: "pending", label: "Pending" },
              { value: "accepted", label: "Accepted" },
              { value: "rejected", label: "Rejected" }
            ]}
          />
        </div>

        {/* Date Filter */}
        <div className="w-full sm:w-[150px] relative">
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Date Preset</label>
          <div className="relative">
            <select
              value={activePreset}
              onChange={(e) => {
                setActivePreset(e.target.value as DatePreset);
                setPage(1);
              }}
              className="w-full bg-[#151929] border border-slate-700 rounded-lg px-3 py-2 pr-8 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors cursor-pointer appearance-none"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400">
              <Calendar size={14} />
            </div>
          </div>
        </div>

        {/* Custom date range fields */}
        {activePreset === "custom" && (
          <>
            <div className="w-full sm:w-[140px]">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Start Date</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-[#151929] border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors [color-scheme:dark]"
              />
            </div>
            <div className="w-full sm:w-[140px]">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">End Date</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-[#151929] border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors [color-scheme:dark]"
              />
            </div>
          </>
        )}
      </div>

      <div className="mb-6">
        <StakeTable
          title=""
          columns={columns}
          data={withdrawals}
          searchPlaceholder="Search by name, uid, or transaction ID..."
          onSearch={(val) => { setSearchTerm(val); setPage(1); }}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onLimitChange={(val) => { setLimit(val); setPage(1); }}
          onPageChange={setPage}
          emptyMessage={isLoading ? "Loading withdrawals..." : "No withdrawals found."}
          hasSidebarMargin={false}
        />
      </div>
    </div>
  );
};

export default Withdrawals;
